import ExcelJS from 'exceljs';

type DataRow<T extends Record<string, string | number>> = T;

/**
 * Metadata to display above the data table in an Excel sheet.
 */
export type SheetMetadata = {
    /** Title of the sheet/report */
    title?: string;
    /** Generation date or report date */
    date?: string;
    /** Short description or additional context */
    description?: string;
};

export interface XlsxOptions<T extends Record<string, string | number>> {
    data: DataRow<T>[];
    columns: Record<keyof T, string>;
    label: string;
    metadata?: SheetMetadata;
    columnWidths?: number[];
}

export const downloadDataXlsx = async <T extends Record<string, string | number>>({
    data,
    columns,
    label,
    metadata,
    columnWidths,
}: XlsxOptions<T>): Promise<void> => {
    if (data.length === 0) {
        console.warn('No data to export.');
        return;
    }

    const keys = Object.keys(columns) as Array<keyof T>;
    const dataKeys = Object.keys(data[0]) as Array<keyof T>;

    if (keys.length !== dataKeys.length || !keys.every((key) => dataKeys.includes(key))) {
        throw new Error('Columns keys must match the keys in data objects.');
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(label);

    // Add metadata rows if provided
    if (metadata) {
        if (metadata.title) {
            worksheet.addRow([`Titre : ${metadata.title}`]);
        }
        if (metadata.date) {
            worksheet.addRow([`Date : ${metadata.date}`]);
        }
        if (metadata.description) {
            worksheet.addRow([`Description : ${metadata.description}`]);
        }
        // Add empty row after metadata if any metadata was added
        if (metadata.title || metadata.date || metadata.description) {
            worksheet.addRow([]);
        }
    }

    // Add header row
    worksheet.addRow(keys.map((key) => columns[key]));

    // Add data rows
    data.forEach((row) => {
        worksheet.addRow(keys.map((key) => row[key]));
    });

    // Set column widths (this should NOT create headers, just set widths)
    keys.forEach((_, index) => {
        const column = worksheet.getColumn(index + 1);
        column.width = columnWidths && columnWidths[index] ? columnWidths[index] : 15;
    });

    // Generate xlsx
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);

    // Create link and download
    const a = document.createElement('a');
    a.href = url;
    a.download = `${label}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
